import { Container, IDestroyOptions, Point, Sprite, TextStyle, Ticker } from "pixi.js";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { VictoryResourcesPackage } from "../resources/VictoryResourcesPackage";
import { GameFont } from "../../../../common/GameFont";
import { AnimatedRewardContainer } from "./AnimatedRewardContainer";
import { SpineResourcesPackage } from "../../../../common/resources/SpineResourcesPackage";
import { CoinsCounter } from "../../../../common/components/CoinsCounter";

export class CoinsReward extends AnimatedRewardContainer {
    private static readonly VALUE_FORMAT = "+{0}";
    private tickAnimationTimeMS = 1000;
    private scaleFactor = 1.2;
    private coinsToSpawn = 3;
    private coinSpawnDelayMS = 150;
    private coinFlyDurationMS = 400;

    private activeTickers = new Set<(_dt: number) => void>();
    private activeCoins: Sprite[] = [];

    protected set textValue(value: number) {
        this.text.text = CoinsReward.VALUE_FORMAT.replace("{0}", value.toString());
    }

    public constructor(
        protected commonResources: CommonResourcesPackage,
        spineResources: SpineResourcesPackage,
        victoryResources: VictoryResourcesPackage,
        protected coinsCounter: CoinsCounter,
        protected coinsFlyContainer: Container,
        protected initialCoinsValue: number
    ) {
        super(
            victoryResources.assets_popups_victory_coins_reward_substrate_png,
            commonResources.assets_common_coin_icon_png,
            CoinsReward.VALUE_FORMAT.replace("{0}", initialCoinsValue.toString()),
            new CoinsTextStyle(),
            (spineResources.assets_spine_coin_coin_glow_json as any).spineData,
        );

        this.text.position.set(123, 24);
    }

    public async playAdAnimation(rewardValue: number, increment: number): Promise<void> {
        // 1) Scale pop animation
        await this.animateScale(1, this.scaleFactor, 300);

        // 2) Setup tick animation
        await this.animateCounterTick(rewardValue);

        // 3) Setup coins fly animation after 60% of tickAnimationTimeMS
        await this.spawnAndFlyCoins();

        // 5) Trigger final animation on counter
        await this.coinsCounter.playFinalAnimation(increment);
    }

    private async animateScale(from: number, to: number, duration: number): Promise<void> {
        return new Promise(resolve => {
            if (this.cancelled) return resolve();

            let elapsed = 0;
            const update = (_dt: number) => {
                if (this.cancelled) {
                    Ticker.shared.remove(update);
                    resolve();
                    return;
                }
                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                this.scale.set(from + (to - from) * t);
                if (t >= 1) {
                    Ticker.shared.remove(update);
                    this.activeTickers.delete(update);
                    resolve();
                }
            };
            this.activeTickers.add(update);
            Ticker.shared.add(update);
        });
    }

    private async animateCounterTick(rewardValue: number): Promise<void> {
        return new Promise(resolve => {
            let elapsed = 0;
            const duration = this.tickAnimationTimeMS;
            const startValue = this.initialCoinsValue;
            const endValue = rewardValue;

            const update = (_dt: number) => {
                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                const current = Math.floor(startValue + (endValue - startValue) * t);
                this.textValue = current;

                if (t >= 1) {
                    Ticker.shared.remove(update);
                    // Reset scale back to 1 after tick complete
                    this.animateScale(this.scaleFactor, 1, 200).then(resolve);
                }
            };

            Ticker.shared.add(update);
        });
    }

    private async spawnAndFlyCoins(): Promise<void> {
        const promises: Promise<void>[] = [];

        for (let i = 0; i < this.coinsToSpawn; i++) {
            const spawnPromise = new Promise<void>(resolve => {
                if (this.cancelled) return resolve();
                let elapsed = 0;

                // Delay before spawning coin
                const delayUpdate = (_dt: number) => {
                    if (this.cancelled) {
                        Ticker.shared.remove(delayUpdate);
                        resolve();
                        return;
                    }
                    elapsed += Ticker.shared.elapsedMS;
                    if (elapsed >= this.coinSpawnDelayMS * i) {
                        Ticker.shared.remove(delayUpdate);

                        // Create coin
                        const coin = new Sprite(this.commonResources.assets_common_coin_icon_png);
                        coin.anchor.set(0.5);

                        const start = this.coinsFlyContainer.toLocal(new Point(this.finalIcon.width * 0.5, this.finalIcon.height * 0.5), this.finalIcon);
                        const target = this.coinsCounter.coinFlyTargetCoordinates;
                        coin.position.copyFrom(start);
                        this.coinsFlyContainer.addChild(coin);
                        this.activeCoins.push(coin);

                        // Fly coin with fixed duration
                        this.flyCoinToTarget(coin, start, target, this.coinFlyDurationMS).then(resolve);
                    }
                };
                this.activeTickers.add(delayUpdate);
                Ticker.shared.add(delayUpdate);
            });

            promises.push(spawnPromise);
        }

        await Promise.all(promises);
    }

    private flyCoinToTarget(coin: Sprite, start: Point, target: Point, duration: number): Promise<void> {
        return new Promise(resolve => {
            if (this.cancelled) return resolve();
            let elapsed = 0;

            const step = (_dt: number) => {
                if (this.cancelled) {
                    Ticker.shared.remove(step);
                    this.activeCoins = this.activeCoins.filter(c => c !== coin);
                    coin.destroy();
                    resolve();
                    return;
                }

                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                coin.x = start.x + (target.x - start.x) * t;

                // Add a parabolic offset
                const arcHeight = -100; // negative goes up
                const parabola = arcHeight * (1 - (2 * t - 1) ** 2);

                coin.y = start.y + (target.y - start.y) * t + parabola;

                coin.scale.set(1 + t * 0.3);

                if (t >= 1) {
                    Ticker.shared.remove(step);
                    this.activeTickers.delete(step);
                    this.activeCoins = this.activeCoins.filter(c => c !== coin);
                    coin.destroy();
                    resolve();
                }
            };
            this.activeTickers.add(step);
            Ticker.shared.add(step);
        });
    }

    public override cancelAnimations(): void {
        this.cancelled = true;

        // Remove all active ticker callbacks
        for (const fn of this.activeTickers) {
            Ticker.shared.remove(fn);
        }
        this.activeTickers.clear();

        // Destroy all flying coins
        for (const coin of this.activeCoins) {
            coin.destroy();
        }
        this.activeCoins = [];
        super.cancelAnimations();
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.cancelAnimations();
        super.destroy(options);
    }

}

class CoinsTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 48,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#7500d4",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}
